import i18next from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';
import {
  formatAmountInput,
  parseNumberInput,
} from '@/utils/approval/expensePreApproval';

/** (구)근저당설정비용 지급 — 레거시 appr_edit_forms_real estate_payment */
export const MORTGAGE_FEE_TYPE = 81;

export const isMortgageFeeType = (apvType: number): boolean =>
  apvType === MORTGAGE_FEE_TYPE;

export const MORTGAGE_FEE_SUBJECT = '부동산 근저당 설정 및 비용 지급에 관한 건';

/** 작성자 언어 제목 — 번역 리소스 로드 전이면 원문 */
export const buildMortgageFeeSubject = (): string =>
  i18next.t(MORTGAGE_FEE_SUBJECT) || MORTGAGE_FEE_SUBJECT;

export type MortgageFeeFormState = {
  brand: string;
  storeName: string;
  storeOpenedAt: string;
  storeOwnerName: string;
  collateralType: string;
  collateralValuationAmount: string;
  collateralDataSource: string;
  collateralPriorLienAmount: string;
  collateralLienPriority: string;
  collateralMaximumSecuredAmount: string;
  collateralRegisteredAt: string;
  collateralAddress: string;
  collateralNetFeeAmount: string;
  collateralVatAmount: string;
};

export type MortgageFeeAmountKey =
  | 'collateralValuationAmount'
  | 'collateralPriorLienAmount'
  | 'collateralMaximumSecuredAmount'
  | 'collateralNetFeeAmount'
  | 'collateralVatAmount';

const AMOUNT_KEYS: MortgageFeeAmountKey[] = [
  'collateralValuationAmount',
  'collateralPriorLienAmount',
  'collateralMaximumSecuredAmount',
  'collateralNetFeeAmount',
  'collateralVatAmount',
];

const isAmountKey = (key: string): key is MortgageFeeAmountKey =>
  (AMOUNT_KEYS as string[]).includes(key);

export const createMortgageFeeState = (): MortgageFeeFormState => ({
  brand: '',
  storeName: '',
  storeOpenedAt: '',
  storeOwnerName: '',
  collateralType: '',
  collateralValuationAmount: '',
  collateralDataSource: '',
  collateralPriorLienAmount: '',
  collateralLienPriority: '',
  collateralMaximumSecuredAmount: '',
  collateralRegisteredAt: '',
  collateralAddress: '',
  collateralNetFeeAmount: '',
  collateralVatAmount: '',
});

const toNumber = (value: string): number => {
  const n = parseNumberInput(value);
  return Number.isFinite(n) ? n : 0;
};

/** 레거시와 같이 빈 칸은 0 — 공급가·부가세 중 하나라도 입력되면 계산값이 생김 */
export const calcMortgageFeeTotals = (
  state: Pick<
    MortgageFeeFormState,
    'collateralNetFeeAmount' | 'collateralVatAmount'
  >,
): { total: number; hqBurden: number } | null => {
  const { collateralNetFeeAmount: net, collateralVatAmount: vat } = state;
  if (net.trim() === '' && vat.trim() === '') return null;
  return {
    total: toNumber(net) + toNumber(vat),
    hqBurden: toNumber(net) * 0.5,
  };
};

export const formatMortgageFeeAmount = (value: unknown): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

/** 조회 화면 — 레거시 number_format 처럼 정수 반올림(.5 는 0 에서 먼 쪽) */
export const formatMortgageFeeViewAmount = (value: unknown): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return formatMortgageFeeAmount(
    Math.sign(value) * Math.round(Math.abs(value)),
  );
};

/** 레거시 검사 순서 그대로. 총비용·본사 부담은 공급가·부가세로 계산돼 검사하지 않음 */
const REQUIRED_FIELDS: {
  key: keyof MortgageFeeFormState;
  message: string;
}[] = [
  { key: 'brand', message: '브랜드를 입력하세요.' },
  { key: 'storeName', message: '매장명을 입력하세요.' },
  { key: 'storeOpenedAt', message: '오픈일자를 입력하세요.' },
  { key: 'storeOwnerName', message: '점주명을 입력하세요.' },
  { key: 'collateralType', message: '물건종류를 입력하세요.' },
  { key: 'collateralValuationAmount', message: '평가총액을 입력하세요.' },
  { key: 'collateralDataSource', message: '자료출처를 입력하세요.' },
  { key: 'collateralPriorLienAmount', message: '선순위금액을 입력하세요.' },
  { key: 'collateralLienPriority', message: '당사담보순위를 입력하세요.' },
  {
    key: 'collateralMaximumSecuredAmount',
    message: '담보설정액을 입력하세요.',
  },
  { key: 'collateralRegisteredAt', message: '담보설정일자를 입력하세요.' },
  { key: 'collateralAddress', message: '담보주소를 입력하세요.' },
  { key: 'collateralNetFeeAmount', message: '공급가를 입력하세요.' },
  { key: 'collateralVatAmount', message: '부가세를 입력하세요.' },
];

export type MortgageFeeViolation = {
  key: (typeof REQUIRED_FIELDS)[number]['key'];
  message: string;
};

export const findMortgageFeeViolation = (
  state: MortgageFeeFormState | null | undefined,
): MortgageFeeViolation | null => {
  const current = state ?? createMortgageFeeState();
  const found = REQUIRED_FIELDS.find(({ key }) => {
    const value = current[key].trim();
    return value === '' || (isAmountKey(key) && value === '-');
  });
  return found ? { key: found.key, message: found.message } : null;
};

/** 임시저장·상신 공통 — 레거시부터 임시저장도 같은 검사 */
export const checkMortgageFeeBeforeSave = (
  state: MortgageFeeFormState | null | undefined,
): string | null => findMortgageFeeViolation(state)?.message ?? null;

const amountOrNull = (value: string): number | null => {
  const n = parseNumberInput(value);
  return Number.isFinite(n) ? n : null;
};

/** 총비용·본사 부담은 서버가 다시 계산해 덮어씀 */
export const buildMortgageFeeAttributes = (
  state: MortgageFeeFormState | null | undefined,
): ApprovalAttributes | null => {
  if (!state) return null;
  const totals = calcMortgageFeeTotals(state);
  const text = (v: string) => v.trim();
  return {
    brand: text(state.brand),
    storeName: text(state.storeName),
    storeOpenedAt: text(state.storeOpenedAt),
    storeOwnerName: text(state.storeOwnerName),
    collateralType: text(state.collateralType),
    collateralValuationAmount: amountOrNull(state.collateralValuationAmount),
    collateralDataSource: text(state.collateralDataSource),
    collateralPriorLienAmount: amountOrNull(state.collateralPriorLienAmount),
    collateralLienPriority: text(state.collateralLienPriority),
    collateralMaximumSecuredAmount: amountOrNull(
      state.collateralMaximumSecuredAmount,
    ),
    collateralRegisteredAt: text(state.collateralRegisteredAt),
    collateralAddress: text(state.collateralAddress),
    collateralNetFeeAmount: amountOrNull(state.collateralNetFeeAmount),
    collateralVatAmount: amountOrNull(state.collateralVatAmount),
    collateralTotalFeeAmount: totals?.total ?? null,
    collateralHqBurdenAmount: totals?.hqBurden ?? null,
  };
};

export type MortgageFeeAttributes = {
  brand: string;
  storeName: string;
  storeOpenedAt: string;
  storeOwnerName: string;
  collateralType: string;
  collateralValuationAmount: number | null;
  collateralDataSource: string;
  collateralPriorLienAmount: number | null;
  collateralLienPriority: string;
  collateralMaximumSecuredAmount: number | null;
  collateralRegisteredAt: string;
  collateralAddress: string;
  collateralNetFeeAmount: number | null;
  collateralVatAmount: number | null;
  collateralTotalFeeAmount: number | null;
  collateralHqBurdenAmount: number | null;
};

const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

const num = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const parseMortgageFeeAttributes = (
  attributes: unknown,
): MortgageFeeAttributes | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const a = attributes as Record<string, unknown>;
  const parsed: MortgageFeeAttributes = {
    brand: str(a.brand),
    storeName: str(a.storeName),
    storeOpenedAt: str(a.storeOpenedAt),
    storeOwnerName: str(a.storeOwnerName),
    collateralType: str(a.collateralType),
    collateralValuationAmount: num(a.collateralValuationAmount),
    collateralDataSource: str(a.collateralDataSource),
    collateralPriorLienAmount: num(a.collateralPriorLienAmount),
    collateralLienPriority: str(a.collateralLienPriority),
    collateralMaximumSecuredAmount: num(a.collateralMaximumSecuredAmount),
    collateralRegisteredAt: str(a.collateralRegisteredAt),
    collateralAddress: str(a.collateralAddress),
    collateralNetFeeAmount: num(a.collateralNetFeeAmount),
    collateralVatAmount: num(a.collateralVatAmount),
    collateralTotalFeeAmount: num(a.collateralTotalFeeAmount),
    collateralHqBurdenAmount: num(a.collateralHqBurdenAmount),
  };
  const hasValue = Object.values(parsed).some((v) => v !== null && v !== '');
  return hasValue ? parsed : null;
};

const amountToInput = (v: number | null): string =>
  v === null ? '' : formatAmountInput(String(v));

export const mortgageFeeAttributesToState = (
  saved: MortgageFeeAttributes | null,
): MortgageFeeFormState => {
  if (!saved) return createMortgageFeeState();
  return {
    brand: saved.brand,
    storeName: saved.storeName,
    storeOpenedAt: saved.storeOpenedAt,
    storeOwnerName: saved.storeOwnerName,
    collateralType: saved.collateralType,
    collateralValuationAmount: amountToInput(saved.collateralValuationAmount),
    collateralDataSource: saved.collateralDataSource,
    collateralPriorLienAmount: amountToInput(saved.collateralPriorLienAmount),
    collateralLienPriority: saved.collateralLienPriority,
    collateralMaximumSecuredAmount: amountToInput(
      saved.collateralMaximumSecuredAmount,
    ),
    collateralRegisteredAt: saved.collateralRegisteredAt,
    collateralAddress: saved.collateralAddress,
    collateralNetFeeAmount: amountToInput(saved.collateralNetFeeAmount),
    collateralVatAmount: amountToInput(saved.collateralVatAmount),
  };
};

export const mortgageFeeKey = (state: MortgageFeeFormState | null): string =>
  state ? JSON.stringify(state) : '';
